import { LinearIcon } from "@storytell/chat-ui/tools";
import { TbChevronDown } from "solid-icons/tb";
import { type Component, createSignal, type JSX, type ParentComponent, Show } from "solid-js";
import { twMerge } from "tailwind-merge";
import { StatusBadge } from "~/components/ui/settings/SettingsDisclosure";
import { useI18n } from "~/i18n/context";

export const GoogleWorkspaceLogo: Component<{ class?: string }> = (props) => (
  <svg class={props.class} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 48 48" aria-hidden="true">
    <path
      fill="#FFC107"
      d="M43.611 20.083H42V20H24v8h11.303c-1.649 4.657-6.08 8-11.303 8-6.627 0-12-5.373-12-12s5.373-12 12-12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 12.955 4 4 12.955 4 24s8.955 20 20 20 20-8.955 20-20c0-1.341-.138-2.65-.389-3.917z"
    />
    <path
      fill="#FF3D00"
      d="M6.306 14.691l6.571 4.819C14.655 15.108 18.961 12 24 12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 16.318 4 9.656 8.337 6.306 14.691z"
    />
    <path
      fill="#4CAF50"
      d="M24 44c5.166 0 9.86-1.977 13.409-5.192l-6.19-5.238C29.211 35.091 26.715 36 24 36c-5.202 0-9.619-3.317-11.283-7.946l-6.522 5.025C9.505 39.556 16.227 44 24 44z"
    />
    <path
      fill="#1976D2"
      d="M43.611 20.083H42V20H24v8h11.303c-.792 2.237-2.231 4.166-4.087 5.571l6.19 5.238C36.971 39.205 44 34 44 24c0-1.341-.138-2.65-.389-3.917z"
    />
  </svg>
);

export const SlackLogo: Component<{ class?: string }> = (props) => (
  <svg class={props.class} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 122.8 122.8" aria-hidden="true">
    <path fill="#e01e5a" d="M25.8 77.6c0 7.1-5.8 12.9-12.9 12.9S0 84.7 0 77.6s5.8-12.9 12.9-12.9h12.9v12.9z" />
    <path
      fill="#e01e5a"
      d="M32.3 77.6c0-7.1 5.8-12.9 12.9-12.9s12.9 5.8 12.9 12.9v32.3c0 7.1-5.8 12.9-12.9 12.9s-12.9-5.8-12.9-12.9V77.6z"
    />
    <path fill="#36c5f0" d="M45.2 25.8c-7.1 0-12.9-5.8-12.9-12.9S38.1 0 45.2 0s12.9 5.8 12.9 12.9v12.9H45.2z" />
    <path
      fill="#36c5f0"
      d="M45.2 32.3c7.1 0 12.9 5.8 12.9 12.9s-5.8 12.9-12.9 12.9H12.9C5.8 58.1 0 52.3 0 45.2s5.8-12.9 12.9-12.9h32.3z"
    />
    <path fill="#2eb67d" d="M97 45.2c0-7.1 5.8-12.9 12.9-12.9s12.9 5.8 12.9 12.9-5.8 12.9-12.9 12.9H97V45.2z" />
    <path
      fill="#2eb67d"
      d="M90.5 45.2c0 7.1-5.8 12.9-12.9 12.9s-12.9-5.8-12.9-12.9V12.9C64.7 5.8 70.5 0 77.6 0s12.9 5.8 12.9 12.9v32.3z"
    />
    <path fill="#ecb22e" d="M77.6 97c7.1 0 12.9 5.8 12.9 12.9s-5.8 12.9-12.9 12.9-12.9-5.8-12.9-12.9V97h12.9z" />
    <path
      fill="#ecb22e"
      d="M77.6 90.5c-7.1 0-12.9-5.8-12.9-12.9s5.8-12.9 12.9-12.9h32.3c7.1 0 12.9 5.8 12.9 12.9s-5.8 12.9-12.9 12.9H77.6z"
    />
  </svg>
);

export const NotionLogo: Component<{ class?: string }> = (props) => (
  <svg class={props.class} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100" fill="none" aria-hidden="true">
    <path
      d="M6.017 4.313l55.333 -4.087c6.797 -0.583 8.543 -0.19 12.817 2.917l17.663 12.443c2.913 2.14 3.883 2.723 3.883 5.053v68.243c0 4.277 -1.553 6.807 -6.99 7.193L24.467 99.967c-4.08 0.193 -6.023 -0.39 -8.16 -3.113L3.3 79.94c-2.333 -3.113 -3.3 -5.443 -3.3 -8.167V11.113c0 -3.497 1.553 -6.413 6.017 -6.8z"
      fill="#fff"
    />
    <path
      fill-rule="evenodd"
      clip-rule="evenodd"
      d="M61.35 0.227l-55.333 4.087C1.553 4.7 0 7.617 0 11.113v60.66c0 2.723 0.967 5.053 3.3 8.167l13.007 16.913c2.137 2.723 4.08 3.307 8.16 3.113l64.257 -3.89c5.433 -0.387 6.99 -2.917 6.99 -7.193V20.64c0 -2.21 -0.873 -2.847 -3.443 -4.733L75.93 3.527c-4.273 -3.107 -6.02 -3.5 -12.817 -2.917zM25.92 19.523c-5.247 0.353 -6.437 0.433 -9.417 -1.99L8.927 11.507c-0.77 -0.78 -0.383 -1.753 1.557 -1.947l53.193 -3.887c4.467 -0.39 6.793 1.167 8.54 2.527l9.123 6.61c0.39 0.197 1.36 1.36 0.193 1.36l-54.933 3.307 -0.68 0.047zM19.803 88.3V30.367c0 -2.53 0.777 -3.697 3.103 -3.893L86 22.78c2.14 -0.193 3.107 1.167 3.107 3.693v57.547c0 2.53 -0.39 4.67 -3.883 4.863l-60.377 3.5c-3.493 0.193 -5.043 -0.97 -5.043 -4.083zm59.6 -54.827c0.387 1.75 0 3.5 -1.75 3.7l-2.91 0.577v42.773c-2.527 1.36 -4.853 2.137 -6.797 2.137 -3.107 0 -3.883 -0.973 -6.21 -3.887l-19.03 -29.94v28.967l6.02 1.363s0 3.5 -4.857 3.5l-13.39 0.777c-0.39 -0.78 0 -2.723 1.357 -3.11l3.497 -0.97v-38.3L30.48 40.667c-0.39 -1.75 0.58 -4.277 3.3 -4.473l14.367 -0.967 19.8 30.327v-26.83l-5.047 -0.58c-0.39 -2.143 1.163 -3.7 3.103 -3.89l13.4 -0.78z"
      fill="#000"
    />
  </svg>
);

// The brand-fill variant of the chat renderers' mark, for the glyph registries
// that are plain .ts modules and so cannot carry the colour class themselves.
export const LinearLogo: Component<{ class?: string }> = (props) => (
  <LinearIcon class={twMerge("text-logo-linear", props.class)} />
);

// authBroken outranks the connected count: a dead credential is invisible from
// the collapsed header otherwise, and the agent keeps answering from the frozen
// corpus behind it. The prop is required, so a provider that omits it fails the
// type check rather than falling back to the connected dot.
export const ConnectionStatusBadge: Component<{ count: number; authBroken: boolean }> = (props) => {
  const { t } = useI18n();
  return (
    <Show
      when={!props.authBroken}
      fallback={<StatusBadge tone="warning">{t("integrations.status_reconnect_required")}</StatusBadge>}
    >
      <Show
        when={props.count > 0}
        fallback={<StatusBadge tone="neutral">{t("integrations.status_not_connected")}</StatusBadge>}
      >
        <StatusBadge tone="positive">{t("integrations.status_connected", { count: String(props.count) })}</StatusBadge>
      </Show>
    </Show>
  );
};

export const SetupInstructionsPanel: ParentComponent<{ title: string }> = (props) => {
  const [open, setOpen] = createSignal(false);

  return (
    <div class="overflow-hidden rounded-xl border border-primary-border bg-panel">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        class="flex w-full items-center justify-between p-6 text-left"
        aria-expanded={open()}
      >
        <span class="text-sm font-medium text-primary-text">{props.title}</span>
        <TbChevronDown class={`size-4 text-muted-text transition-transform ${open() ? "rotate-180" : ""}`} />
      </button>

      <Show when={open()}>
        <div class="flex flex-col gap-6 border-t border-primary-border px-6 pt-4 pb-6">{props.children}</div>
      </Show>
    </div>
  );
};

export const SetupStepRow: Component<{
  index: number;
  title: string;
  imageSrc?: string;
  children?: JSX.Element;
}> = (props) => {
  return (
    <div class="flex flex-col gap-4 sm:flex-row sm:items-start sm:gap-6">
      <div class="min-w-0 flex-1 space-y-2">
        <div class="flex items-center gap-2">
          <span class="flex size-6 items-center justify-center rounded-full bg-brand text-xs font-semibold text-on-brand">
            {props.index}
          </span>
          <span class="text-sm font-medium text-primary-text">{props.title}</span>
        </div>
        <div class="ml-8 space-y-2">{props.children}</div>
      </div>
      <Show when={props.imageSrc}>
        <div class="flex w-full shrink-0 justify-center sm:w-75 sm:max-w-75 sm:justify-end">
          <img
            src={props.imageSrc}
            alt=""
            width={300}
            class="h-auto w-full max-w-75 rounded-lg border border-primary-border object-contain"
            loading="lazy"
          />
        </div>
      </Show>
    </div>
  );
};
